import clean from 'lodash-clean';
import get from 'lodash/get';
import pick from 'lodash/pick';
import { SubmissionError } from 'redux-form';

import i18n from 'shared/lib/i18n';

import {
  ADD_PAYMENT_PROVIDER_FAILURE,
  ADD_PAYMENT_PROVIDER_REQUEST,
  ADD_PAYMENT_PROVIDER_SUCCESS,
  BUILDING_RESIDENTS_REQUESTED,
  GET_BMX_REGISTRATION_STATUS_FAILURE,
  GET_BMX_REGISTRATION_STATUS_REQUEST,
  GET_BMX_REGISTRATION_STATUS_SUCCESS,
  GET_ITEMS_FOR_EXPORT_SUCCESS,
  GET_PERSON_NOTIFICATIONS_FAILURE,
  GET_PERSON_NOTIFICATIONS_REQUEST,
  GET_PERSON_NOTIFICATIONS_SUCCESS,
  IMPORT_RESIDENT_FAILURE,
  IMPORT_RESIDENT_REQUEST,
  IMPORT_RESIDENT_SUCCESS,
  PERSON_ACTIVITY_FAILURE,
  PERSON_ACTIVITY_OVERVIEW_FAILURE,
  PERSON_ACTIVITY_OVERVIEW_REQUEST,
  PERSON_ACTIVITY_OVERVIEW_SUCCESS,
  PERSON_ACTIVITY_REQUEST,
  PERSON_ACTIVITY_SUCCESS,
  REMOVE_RESIDENT_ROLE_FAILURE,
  REMOVE_RESIDENT_ROLE_REQUEST,
  REMOVE_RESIDENT_ROLE_SUCCESS,
  RESEND_BMX_INVITE_FAILURE,
  RESEND_BMX_INVITE_REQUEST,
  RESEND_BMX_INVITE_SUCCESS,
  RESEND_INVITE_TO_ALL_RESIDENTS_FAILURE,
  RESEND_INVITE_TO_ALL_RESIDENTS_INFO_FAILURE,
  RESEND_INVITE_TO_ALL_RESIDENTS_INFO_REQUEST,
  RESEND_INVITE_TO_ALL_RESIDENTS_INFO_SUCCESS,
  RESEND_INVITE_TO_ALL_RESIDENTS_REQUEST,
  RESEND_INVITE_TO_ALL_RESIDENTS_SUCCESS,
  RESIDENT_CREATE_FAILURE,
  RESIDENT_CREATE_REQUEST,
  RESIDENT_CREATE_SUCCESS,
  RESIDENT_MOVE_OUT_FAILURE,
  RESIDENT_MOVE_OUT_REQUEST,
  RESIDENT_MOVE_OUT_SUCCESS,
  RESIDENT_UPDATE_FAILURE,
  RESIDENT_UPDATE_REQUEST,
  RESIDENT_UPDATE_SUCCESS,
  SELECT_EXISTING_LOAD_PEOPLE,
  SET_MODAL_TYPE,
  SET_SKIP_MOVE_IN_FAILURE,
  SET_SKIP_MOVE_IN_REQUEST,
  SET_SKIP_MOVE_IN_SUCCESS,
} from '../../actionConstants';
import { uploadImage } from '../Visitor/actionCreators';

import { sendGA4Event } from '../Shared/utils/ga';

import apiClient from '../../apiClient';
import { history } from '../../store';
import { CONFIRM } from '../Modal/modalTypes';

export const resendInviteToAllResidentsInfo = (dispatch, buildingId) =>
  dispatch({
    type: RESEND_INVITE_TO_ALL_RESIDENTS_INFO_REQUEST,
    payload: apiClient.building.resendInviteToAllResidentsInfo(buildingId).then(
      (res) =>
        dispatch({
          type: RESEND_INVITE_TO_ALL_RESIDENTS_INFO_SUCCESS,
          res,
          buildingId,
        }),
      (err) =>
        dispatch({
          type: RESEND_INVITE_TO_ALL_RESIDENTS_INFO_FAILURE,
          err,
        }),
    ),
  });

export const resendInviteToAllResidents = (
  dispatch,
  buildingId,
  notVerifiedResidents,
) =>
  dispatch({
    type: RESEND_INVITE_TO_ALL_RESIDENTS_REQUEST,
    payload: apiClient.building.resendInviteToAllResidents(buildingId).then(
      () => {
        resendInviteToAllResidentsInfo(dispatch, buildingId);
        return dispatch({
          type: RESEND_INVITE_TO_ALL_RESIDENTS_SUCCESS,
          successMessage: i18n.t('resident.sentInvites', {
            notVerifiedResidents,
          }),
        });
      },
      (err) =>
        dispatch({
          type: RESEND_INVITE_TO_ALL_RESIDENTS_FAILURE,
          err,
        }),
    ),
  });

export const promptForResendInviteToAllResidents = (
  dispatch,
  buildingId,
  usersCount,
) => {
  const notVerifiedResidents = get(usersCount, 'data.notVerified', '');
  const modalProps = {
    bodyText: i18n.t('resident.resendInviteToAll', {
      notVerifiedResidents,
    }),
    confirmText: i18n.t('common.yes'),
    cancelText: i18n.t('common.no'),
    onConfirm: () =>
      resendInviteToAllResidents(dispatch, buildingId, notVerifiedResidents),
  };
  return dispatch({
    type: SET_MODAL_TYPE,
    modalType: CONFIRM,
    modalProps,
  });
};

export const addProviderId = (
  dispatch,
  values,
  personId,
  callback,
  isSilent = false,
  withoutLoading = false,
) => {
  const data = {
    role: values.role,
    unit: values.property,
    paymentProviderId: values.paymentProviderId,
  };
  dispatch({
    type: ADD_PAYMENT_PROVIDER_REQUEST,
    withoutLoading,
    payload: apiClient.resident.updateRoleInfo(personId, data).then(
      (res) => {
        callback && callback(res);
        if (isSilent) return true;
        return dispatch({
          type: ADD_PAYMENT_PROVIDER_SUCCESS,
          successMessage: i18n.t('resident.paymentProviderAdded'),
        });
      },
      (err) => {
        dispatch({
          type: ADD_PAYMENT_PROVIDER_FAILURE,
          err,
        });
      },
    ),
  });
};

export const updateResidentRoleInfo = (dispatch, personId, data, callback) =>
  dispatch({
    type: RESIDENT_UPDATE_REQUEST,
    payload: apiClient.resident.updateRoleInfo(personId, data).then(
      (res) => {
        dispatch({
          type: RESIDENT_UPDATE_SUCCESS,
          successMessage: i18n.t('resident.residentUpdated'),
        });
        return callback && callback(res);
      },
      (err) =>
        dispatch({
          type: RESIDENT_UPDATE_FAILURE,
          err,
        }),
    ),
  });

export const createResident = (dispatch, rawData) => {
  const { images, ...data } = rawData;
  return dispatch({
    type: RESIDENT_CREATE_REQUEST,
    isForm: true,
    payload: apiClient.resident.create(data).then(
      async (res) => {
        if (res.data && res.data.created) {
          sendGA4Event('created_resident');
          dispatch({
            type: RESIDENT_CREATE_SUCCESS,
            successMessage: i18n.t('resident.residentCreated'),
          });
          const personId = res.data.id;
          const { paymentProviderId } = data;
          paymentProviderId && addProviderId(dispatch, data, personId);

          const url = `persons/${personId}/photo/`;
          await uploadImage(dispatch, images, url);

          return history.goBack();
        }
        if (res.data && !res.data.created) {
          dispatch({
            type: SELECT_EXISTING_LOAD_PEOPLE,
            people: res.data.persons,
          });
          throw new SubmissionError({
            _error: i18n.t('resident.residentAlreadyExists'),
          });
        }
        return res;
      },
      (err) => {
        dispatch({
          type: RESIDENT_CREATE_FAILURE,
          err,
        });
        return err;
      },
    ),
  });
};

export const setSkipMoveIn = (dispatch, residentId, skipMoveIn, callback) =>
  dispatch({
    type: SET_SKIP_MOVE_IN_REQUEST,
    payload: apiClient.resident.setSkipMoveIn(residentId, { skipMoveIn }).then(
      () => {
        dispatch({
          type: SET_SKIP_MOVE_IN_SUCCESS,
          successMessage: i18n.t('resident.skipMoveInIsSet'),
        });
        return callback();
      },
      (err) => dispatch({ type: SET_SKIP_MOVE_IN_FAILURE, err }),
    ),
  });

export const updateResident = (dispatch, residentId, rawData) => {
  const { images, ...rest } = rawData;
  const data = pick(rest, [
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'addAsBoardMember',
    'property',
    'photo_deleted',
  ]);
  return dispatch({
    type: RESIDENT_UPDATE_REQUEST,
    isForm: true,
    payload: apiClient.resident.update(residentId, clean(data)).then(
      async (res) => {
        dispatch({
          type: RESIDENT_UPDATE_SUCCESS,
          successMessage: i18n.t('resident.residentUpdated'),
        });
        const personId = res.data.id;
        const url = `persons/${personId}/photo/`;
        await uploadImage(dispatch, images, url);
        return history.goBack();
      },
      (err) => {
        dispatch({
          type: RESIDENT_UPDATE_FAILURE,
          err,
        });
        return err;
      },
    ),
  });
};

export const addRole = (
  dispatch,
  values,
  callback,
  shouldSkipInvite = true,
  isSilent = false,
  withoutLoading = false,
) => {
  const data = {
    skipInvite: shouldSkipInvite,
    person: values.id,
    property: values.property,
    role: values.role,
    addAsBoardMember: values.addAsBoardMember,
  };
  return dispatch({
    type: RESIDENT_UPDATE_REQUEST,
    withoutLoading,
    payload: apiClient.resident.addRole(data).then(
      (res) => {
        callback && callback(res);
        if (isSilent) return true;
        return dispatch({
          type: RESIDENT_UPDATE_SUCCESS,
          successMessage: i18n.t('resident.roleAdded'),
        });
      },
      (err) => {
        dispatch({
          type: RESIDENT_UPDATE_FAILURE,
          err,
        });
        return false;
      },
    ),
  });
};

export const askForSendingInvite = (dispatch, values, callback) => {
  const modalProps = {
    confirmText: i18n.t('common.yes'),
    cancelText: i18n.t('common.no'),
    bodyText: i18n.t('resident.sendInvite', {
      firstName: values.firstName,
      lastName: values.lastName,
    }),
    onConfirm: () => addRole(dispatch, values, callback, false),
    onCancel: () => addRole(dispatch, values, callback, true),
    hideCloseButton: true,
  };
  dispatch({
    type: SET_MODAL_TYPE,
    modalType: CONFIRM,
    modalProps,
  });
};

export const fetchBuildingResidents = (
  dispatch,
  buildingId,
  isPackageRecipients,
) => {
  const request = isPackageRecipients
    ? apiClient.building.recipientsWithoutPagination
    : apiClient.building.residentsWithoutPagination;
  return dispatch({
    type: BUILDING_RESIDENTS_REQUESTED,
    buildingId,
    payload: request(buildingId),
  });
};

export const moveOutResident = (dispatch, personId, role, props) => {
  const buildingId = get(props, 'match.params.buildingId', null);
  let removeMethodName = 'moveOut';
  let data = {
    property: role.propertyId,
    role: role.role,
  };

  // if board member (role id - 7)
  if (data.role === 7) {
    removeMethodName = 'moveOutBoardMember';
    data = { buildingId };
  }

  return dispatch({
    type: RESIDENT_MOVE_OUT_REQUEST,
    payload: apiClient.resident[removeMethodName](personId, data).then(
      (res) => {
        dispatch({
          type: RESIDENT_MOVE_OUT_SUCCESS,
          successMessage: i18n.t('resident.residentMovedOut'),
        });
        fetchBuildingResidents(dispatch, buildingId); // TODO is it required?
        return res;
      },
      (err) =>
        dispatch({
          type: RESIDENT_MOVE_OUT_FAILURE,
          err,
        }),
    ),
  });
};

export const resendBmxInvite = (dispatch, residentId) =>
  dispatch({
    type: RESEND_BMX_INVITE_REQUEST,
    payload: apiClient.resident.resendBMXInvite(residentId).then(
      (res) => {
        dispatch({
          type: RESEND_BMX_INVITE_SUCCESS,
        });
        return res;
      },
      (err) => {
        dispatch({
          type: RESEND_BMX_INVITE_FAILURE,
          err,
        });
        return err;
      },
    ),
  });

export const getBmxRegistrationStatus = (dispatch, residentId) =>
  dispatch({
    type: GET_BMX_REGISTRATION_STATUS_REQUEST,
    payload: apiClient.resident.isRegisteredWithBMX(residentId).then(
      (res) => {
        dispatch({
          type: GET_BMX_REGISTRATION_STATUS_SUCCESS,
        });
        return res;
      },
      (err) => {
        dispatch({
          type: GET_BMX_REGISTRATION_STATUS_FAILURE,
          err,
        });
        return err;
      },
    ),
  });

export const getResidentActivity = (
  dispatch,
  queryParams,
  buildingId,
  personId,
) =>
  dispatch({
    type: PERSON_ACTIVITY_REQUEST,
    isTodayCount: queryParams?.todayCount,
    payload: apiClient.resident
      .activity(buildingId, personId, queryParams)
      .then(
        (res) => {
          dispatch({
            type: PERSON_ACTIVITY_SUCCESS,
            res,
          });
          return res;
        },
        (err) =>
          dispatch({
            type: PERSON_ACTIVITY_FAILURE,
            err,
          }),
      ),
  });

export const getResidentNotifications = (
  dispatch,
  queryParams,
  buildingId,
  personId,
  isExport,
) =>
  dispatch({
    type: GET_PERSON_NOTIFICATIONS_REQUEST,
    withoutLoading: isExport,
    payload: apiClient.resident
      .getNotifications(buildingId, personId, queryParams)
      .then(
        (res) => {
          dispatch({
            type: isExport
              ? GET_ITEMS_FOR_EXPORT_SUCCESS
              : GET_PERSON_NOTIFICATIONS_SUCCESS,
            res,
            personId,
          });
          return res;
        },
        (err) =>
          dispatch({
            type: GET_PERSON_NOTIFICATIONS_FAILURE,
            err,
          }),
      ),
  });

export const getResidentActivityOverview = (dispatch, buildingId, personId) =>
  dispatch({
    type: PERSON_ACTIVITY_OVERVIEW_REQUEST,
    payload: apiClient.resident.activityOverview(buildingId, personId).then(
      (res) =>
        dispatch({
          type: PERSON_ACTIVITY_OVERVIEW_SUCCESS,
          res,
          buildingId,
          personId,
        }),
      (err) =>
        dispatch({
          type: PERSON_ACTIVITY_OVERVIEW_FAILURE,
          err,
        }),
    ),
  });

export const removeResidentRole = (dispatch, residentId, data) =>
  dispatch({
    type: REMOVE_RESIDENT_ROLE_REQUEST,
    withoutLoading: true,
    payload: apiClient.resident.moveOut(residentId, data).then(
      () => {
        dispatch({
          type: REMOVE_RESIDENT_ROLE_SUCCESS,
        });
        return true;
      },
      (err) => {
        dispatch({
          type: REMOVE_RESIDENT_ROLE_FAILURE,
          err,
        });
        return false;
      },
    ),
  });

export const getResidentNameAndContacts = ({
  firstName,
  lastName,
  contactInfo: contacts,
}) => {
  const name = `${firstName} ${lastName}`;
  const contactData = contacts
    .reverse()
    .map((contact) => contact.contactInfo)
    .join(', ');
  return `${name} (${contactData})`;
};

export const importResident = (dispatch, data) => {
  const name = `${data.firstName} ${data.lastName}`;
  return dispatch({
    type: IMPORT_RESIDENT_REQUEST,
    withoutLoading: true,
    payload: apiClient.resident.create(clean(data)).then(
      async (res) => {
        const { paymentProviderId } = data;
        if (res.data && res.data.created) {
          dispatch({
            type: IMPORT_RESIDENT_SUCCESS,
          });
          if (paymentProviderId) {
            const personId = res.data.id;
            await addProviderId(dispatch, data, personId, null, true, true);
          }
          return true;
        }
        if (
          res.data &&
          !res.data.created &&
          res.data.persons &&
          res.data.persons[0]
        ) {
          const personId = res.data.persons[0].id;
          const newRoleData = { id: personId, ...data };
          const shouldSkipInvite = data.skipInvite;
          const isRoleAdded = await addRole(
            dispatch,
            newRoleData,
            null,
            shouldSkipInvite,
            true,
            true,
          );
          if (isRoleAdded && paymentProviderId) {
            await addProviderId(dispatch, data, personId, null, true, true);
          }
          return isRoleAdded;
        }
        return i18n.t('resident.isNotCreated', { name });
      },
      (err) => {
        dispatch({
          type: IMPORT_RESIDENT_FAILURE,
          err,
        });
        return i18n.t('resident.isNotCreated', { name });
      },
    ),
  });
};

export const confirmImport = (dispatch, importInfo, onConfirm, data) => {
  const modalProps = {
    bodyText: importInfo,
    onConfirm: () => onConfirm(data),
  };
  dispatch({
    type: SET_MODAL_TYPE,
    modalType: CONFIRM,
    modalProps,
  });
};
